import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import {
  CustomDialogProps,
  SmartAutoComplete,
  SmartForm,
  SmartSubmit,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ApiResponse, ReturnNodeDto, useFileApi } from "~/api/GeneratedApi";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

interface Props {
  sourceNode: ReturnNodeDto;
  onSubmitted: () => void;
}

interface FormProps {
  destinationId: number;
}

const MoveNodePopupForm = ({
  sourceNode,
  onSubmitted,
  handleClose,
}: CustomDialogProps & Props): JSX.Element => {
  const { t } = useTranslation("file-explorer");
  const { enqueueSnackbar } = useSnackbar();

  const fileApi = useFileApi();

  const { isLoading: isLoadingNodes, data: nodes } = useCustomQuery(
    "files",
    () => fileApi.getNodes(),
  );

  const onSuccess = () => {
    enqueueSnackbar(t("item-moved"), {
      variant: "success",
    });
    handleClose?.();
    onSubmitted();
  };

  const onSubmit = ({ destinationId }: FormProps) => {
    return fileApi.moveItem({
      source: sourceNode.id,
      destination: destinationId,
    });
  };

  const getAllChildren = useCallback((node: ReturnNodeDto): ReturnNodeDto[] => {
    const currValues = [node];

    node.childNodes?.forEach((x) => currValues.push(...getAllChildren(x)));

    return currValues;
  }, []);

  const defaultValues = useMemo(
    () => ({ destinationId: sourceNode.parentId! }),
    [sourceNode.parentId],
  );

  return (
    <Dialog open onClose={handleClose} aria-labelledby="form-dialog-title">
      <SmartForm<FormProps, ApiResponse>
        onSubmit={onSubmit}
        noGrid
        onSuccess={onSuccess}
        formProps={{
          defaultValues,
        }}
      >
        <DialogTitle id="form-dialog-title">
          {t("move-node", { node: sourceNode.fullName })}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SmartAutoComplete<FormProps, number, false>
                name="destinationId"
                gridProps={{ xs: 12 }}
                disabled={isLoadingNodes}
                label={t("destination")}
                options={getAllChildren(nodes!.nodes!)
                  .filter(
                    (x) =>
                      x.isFolder && !x.programManaged && x.id !== sourceNode.id,
                  )
                  .map((x) => x.id)}
                getOptionLabel={(o) => {
                  const node = getAllChildren(nodes!.nodes!)
                    ?.filter(
                      (x) =>
                        x.isFolder &&
                        !x.programManaged &&
                        x.id !== sourceNode.id,
                    )
                    .find((x) => x.id === o);
                  return node?.name ?? t("general:unknown");
                }}
                controllerProps={{
                  rules: {
                    validate: (val: FormProps["destinationId"]) => {
                      if (val === sourceNode.id) {
                        return t("cant-move-inside-itself").toString();
                      }
                      if (val === sourceNode.parentId) {
                        return t("node-is-already-in-folder").toString();
                      }
                      return true;
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("general:cancel")}
          </Button>
          <SmartSubmit>{t("move")}</SmartSubmit>
        </DialogActions>
      </SmartForm>
    </Dialog>
  );
};

export default MoveNodePopupForm;
